import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import {
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaUserTie,
  FaSignOutAlt,
} from "react-icons/fa";
import { signOut } from "aws-amplify/auth";

import translations from "../translation";
import Dashboard from "../component/admin/Dashboard";
import PendingTable from "../component/admin/PendingTable";
import ApprovedTable from "../component/admin/ApprovedTable";
import RejectedTable from "../component/admin/RejectedTable";
import ActiveUsersTable from "../component/admin/ActiveUsersTable";
import ApproversTable from "../component/admin/ApproversTable";
import Form from "../component/user/Form";
import "../App.css";
import "bootstrap/dist/css/bootstrap.min.css";

const AdminDashboard = () => {
  const [userName, setUserName] = useState("John Doe");
  const [userRole, setUserRole] = useState("Admin");
  const [language, setLanguage] = useState("en");

  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));

  // Translate Function
  const translate = (key) => translations[language]?.[key] || key;

  // Handle Language Change
  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  // Update Document Title Based on Route
  useEffect(() => {
    const pageTitleMap = {
      "/admin/dashboard": translate("adminDashboard"),
      "/admin/users": translate("totalUsers"),
      "/admin/approvers": translate("approvers"),
      "/admin/pending": translate("pendingAnnouncements"),
      "/admin/approved": translate("approvedAnnouncements"),
      "/admin/rejected": translate("rejectedAnnouncements"),
      "/admin/create-announcement": translate("createAnnouncement"),
    };

    document.title = pageTitleMap[location.pathname] || translate("adminDashboard");
  }, [location.pathname, language]);

  // Update User Info and Set RTL/LTR
  useEffect(() => {
    if (user) {
      setUserName(user.fullname || translate("unknownUser"));
      setUserRole(user.role || translate("defaultRole"));
    }

    // Update text direction dynamically
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [user, language]);

  const getLinkClasses = (path) => {
    const isActive = location.pathname === path;
    return `flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive
      ? "text-gray-700 bg-gray-200"
      : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
      }`;
  };

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.removeItem("user");
      window.location.href = "/login";
    } catch (error) {
      console.error("Error logging out: ", error);
    }
  };

  return (
    <div
      className={`flex ${language === "ar" ? "flex-row-reverse text-arabic" : "flex-row"}`}
    >
      {/* Sidebar */}
      <div
        className={`bg-white h-screen fixed ${language === "ar" ? "right-0 text-arabic" : "left-0"
          } border-r border-gray-200 flex flex-col justify-between w-64`}
      >
        <div>
          {/* Logo */}
          <div className="flex items-center p-4 border-b border-gray-200">
            <img
              className="h-28 w-70 mt-2"
              src="/images/alnaghi.png"
              alt={translate("logoAlt")}
            />
          </div>

          {/* Navigation Links */}
          <nav className="mt-4">
            <Link to="/admin/dashboard" className={getLinkClasses("/admin/dashboard")}>
              <FaChartLine className="w-5 h-5 mr-3" />
              {translate("dashboard")}
            </Link>
            <Link to="/admin/users" className={getLinkClasses("/admin/users")}>
              <FaUsers className="w-5 h-5 mr-3" />
              {translate("users")}
            </Link>
            <Link
              to="/admin/create-announcement"
              className={getLinkClasses("/admin/create-announcement")}
            >
              <FaUserTie className="w-5 h-5 mr-3" />
              {translate("createAnnouncement")}
            </Link>
            <Link to="/admin/pending" className={getLinkClasses("/admin/pending")}>
              <FaClock className="w-5 h-5 mr-3" />
              {translate("pendingAnnouncements")}
            </Link>
            <Link to="/admin/approved" className={getLinkClasses("/admin/approved")}>
              <FaCheckCircle className="w-5 h-5 mr-3" />
              {translate("approvedAnnouncements")}
            </Link>
            <Link to="/admin/rejected" className={getLinkClasses("/admin/rejected")}>
              <FaTimesCircle className="w-5 h-5 mr-3" />
              {translate("rejectedAnnouncements")}
            </Link>
          </nav>
        </div>

        {/* Footer Section */}
        <div className="p-4 border-t border-gray-200">
          <select
            className="w-full text-sm bg-gray-100 border border-gray-300 text-gray-700 py-2 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
            value={language}
            onChange={handleLanguageChange}
          >
            <option value="en">{translate("languageEnglish")}</option>
            <option value="ar">{translate("languageArabic")}</option>
          </select>
          <div className="flex items-center mb-4">
            <img
              className="h-10 w-10 rounded-full border border-gray-300"
              src="https://cdn.pixabay.com/photo/2020/07/01/12/58/icon-5359553_1280.png"
              alt={translate("userAvatarAlt")}
            />
            <div className="ml-3">
              <p className="text-lg font-semibold text-gray-700">{userName}</p>
              <p className="text-sm text-gray-500">{translate("adminRole")}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            <FaSignOutAlt className="w-5 h-5 mr-3" />
            {translate("logout")}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className={`flex-grow ${language === "ar" ? "mr-64 text-arabic" : "ml-64"} w-100 px-4 sm:px-6 lg:px-8 py-8`}>
        <Routes>
          <Route path="dashboard" element={<Dashboard language={language} />} />
          <Route path="users" element={<ActiveUsersTable language={language} />} />
          <Route path="approvers" element={<ApproversTable language={language} />} />
          <Route path="pending" element={<PendingTable language={language} />} />
          <Route path="approved" element={<ApprovedTable language={language} />} />
          <Route path="rejected" element={<RejectedTable language={language} />} />
          <Route path="create-announcement" element={<Form language={language} />} />
          <Route path="/" element={<Navigate to="dashboard" />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
